// ui/ConfirmModal.js
import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Spinner from "./Spinner";
import { logError } from "../utils/logger";
import ErrorBanner from "./ErrorBanner";

/**
 * Reusable confirmation modal for destructive actions
 *
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Called when user cancels or clicks backdrop
 * @param {function} onConfirm - Called when user confirms action
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} confirmVariant - Button style: "danger" (red) or "normal" (indigo)
 */
export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title = "Confirm action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  confirmVariant = "danger",
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  if (!open) return null;

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      setError(null);

      await Promise.resolve(onConfirm());

      // Only close if its successful
      onClose();
    } catch (error) {
      // Show error in modal, keep open for retry
      setError(error.message || "Operation failed. Please try again.");
      logError("Confirmation action failed: ", error);
    } finally {
      setConfirming(false);
    }
  };

  const handleModalClose = () => {
    if (confirming) return; // Prevent close during operation
    onClose();
  };

  const isDanger = confirmVariant === "danger";
  const headerBgClass = isDanger ? "bg-destructive-50" : "bg-primary-50";
  const headerTextClass = isDanger ? "text-destructive-700" : "text-primary";
  const buttonBgClass = isDanger
    ? "bg-destructive hover:bg-destructive-hover"
    : "bg-primary hover:bg-primary-hover";
  const hoverBgClass = isDanger
    ? "hover:bg-destructive-100"
    : "hover:bg-primary-100";

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={handleModalClose}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-md rounded-2xl bg-surface-elevated border border-border shadow-2xl overflow-hidden">
        {/* Header */}
        <header
          className={`flex items-center justify-between px-5 py-3 border-b border-border ${headerBgClass}`}
        >
          <div className={`flex items-center gap-2 ${headerTextClass}`}>
            <ExclamationTriangleIcon className="h-5 w-5" />
            <h3 className="text-sm font-semibold">{title}</h3>
          </div>
          <button
            onClick={handleModalClose}
            className={`p-1 rounded ${hoverBgClass} disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label="Close"
            disabled={confirming}
          >
            <XMarkIcon className={`h-5 w-5 ${headerTextClass}`} />
          </button>
        </header>

        {/* Body */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-text-secondary">{message}</p>
          {error && (
            <ErrorBanner
              message={error}
              variant="critical"
              onRetry={handleConfirm}
              onDismiss={() => setError(null)}
            />
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            <button
              className="px-4 py-2 rounded-md border border-border-strong text-sm font-medium hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleModalClose}
              disabled={confirming}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirming}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-text-inverse disabled:opacity-50 disabled:cursor-not-allowed ${buttonBgClass}`}
            >
              {confirming && <Spinner size={16} />}
              {confirming ? "Deleting..." : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
